import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Textarea, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type {
  ActionRequest,
  AssistantStep,
  AssistantTurn,
  Decision,
  PendingApprovalData,
  ReviewConfig,
  UserTurn,
} from "./types";

// =========================
// AgentLivingLogo — "살아있는 뇌" (top-down view) MBCT/CBT × Entity feel
//   · 뇌 실루엣 (좌우 hemisphere) — 3겹으로 그려서 와이어 두께감
//   · 종단 fissure — 중앙 골이 호흡하며 흐름
//   · gyri 곡선 (뇌주름) — 각 hemisphere 안에서 살짝 흔들림
//   · 내부에 떠도는 신경 입자 12개 — 다른 속도, 뇌 안 범위 안에 머무름
//   · 중심 코어 — 다층 halo + flicker (의식의 가운데)
//   · 가끔 sweep 라인이 한 바퀴 돎 (알아차림 스캔)
//   모든 timing 이 lockstep 이 아니라 organic.
// =========================

const BRAIN_OUTLINE =
  "M 40 12 C 30 9, 17 14, 12 26 C 7 38, 10 52, 19 60 C 26 66, 35 66, 40 62 C 45 66, 54 66, 61 60 C 70 52, 73 38, 68 26 C 63 14, 50 9, 40 12 Z";

const BRAIN_FISSURE = "M 40 12 C 38 22, 42 30, 40 40 C 38 50, 42 58, 40 62";

const GYRI_LEFT = [
  "M 16 30 Q 22 27, 28 31 T 36 29",
  "M 14 42 Q 22 39, 28 44 T 36 42",
  "M 18 52 Q 24 49, 30 54",
  "M 20 22 Q 26 19, 32 22",
];

const GYRI_RIGHT = [
  "M 64 30 Q 58 27, 52 31 T 44 29",
  "M 66 42 Q 58 39, 52 44 T 44 42",
  "M 62 52 Q 56 49, 50 54",
  "M 60 22 Q 54 19, 48 22",
];

export function AgentLivingLogo({
  size = 44,
  active = true,
}: {
  size?: number;
  active?: boolean;
}) {
  const primary = theme.vars.palette.primary.solid;
  const soft = theme.vars.palette.primary.soft;

  // 16 신경 입자 — 각자 고유한 chaotic trajectory
  //   주요 회전 + 부 sinusoidal 각도 jitter + 반경 진동 합성
  //   서로 다른 주기/위상이 겹쳐서 lockstep 절대 안 됨
  const particles = Array.from({ length: 16 }, (_, i) => ({
    baseR: 6 + ((i * 2.7) % 14), // 기본 반경
    rAmp: 2 + ((i * 0.8) % 5), // 반경 진동 amplitude
    rFreq: 1 + (i % 3), // 반경 sinusoid 주파수 (정수, loop 매끄럽게)
    angAmp: 18 + ((i * 7) % 32), // 각속도 비균질성 (degrees)
    angFreq: 1 + ((i + 1) % 3), // 각도 jitter 주파수 (정수)
    dur: 5.5 + ((i * 1.3) % 9), // 한 cycle 시간
    startAngle: (i * 137.5) % 360, // golden-angle spacing
    phase: (i * 0.41) % (Math.PI * 2), // sinusoid 위상 offset
    size: 0.45 + ((i * 0.13) % 0.7),
    opacity: 0.4 + ((i * 0.07) % 0.5),
    dir: i % 2 === 0 ? 1 : -1,
  }));

  // 각 입자별 keyframes 생성 — 12 stops 의 chaotic 궤도
  const particleAnimations: Record<string, unknown> = {};
  const NUM_STOPS = 12;
  particles.forEach((p, i) => {
    const kfName = `agent-pt-${i}`;
    const stops: Record<string, { transform: string }> = {};
    for (let j = 0; j <= NUM_STOPS; j++) {
      const t = j / NUM_STOPS;
      // 각속도: 균일 회전 + sinusoidal jitter
      const angleDeg =
        p.startAngle +
        t * 360 * p.dir +
        Math.sin(t * Math.PI * 2 * p.angFreq + p.phase) * p.angAmp;
      // 반경: base + sinusoidal 진동
      const r =
        p.baseR + Math.sin(t * Math.PI * 2 * p.rFreq + p.phase * 1.7) * p.rAmp;
      const rad = (angleDeg * Math.PI) / 180;
      const x = r * Math.cos(rad);
      const y = r * Math.sin(rad);
      stops[`${Math.round(t * 100)}%`] = {
        transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`,
      };
    }
    particleAnimations[`@keyframes ${kfName}`] = stops;
  });

  return (
    <Box
      component="svg"
      viewBox="0 0 80 80"
      sx={{
        width: size * (80 / 44),
        height: size * (80 / 44),
        overflow: "visible",
        ...particleAnimations,
        "@keyframes agent-brain-breath": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.035)" },
        },
        "@keyframes agent-brain-breath-slow": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.02)" },
        },
        "@keyframes agent-gyri-fade": {
          "0%, 100%": { opacity: 0.35 },
          "50%": { opacity: 0.75 },
        },
        "@keyframes agent-gyri-fade-2": {
          "0%, 100%": { opacity: 0.55 },
          "50%": { opacity: 0.25 },
        },
        "@keyframes agent-fissure-flow": {
          "0%, 100%": { strokeDashoffset: 0, opacity: 0.55 },
          "50%": { strokeDashoffset: -6, opacity: 0.85 },
        },
        "@keyframes agent-core-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 1 },
          "50%": { transform: "scale(1.22)", opacity: 0.85 },
        },
        "@keyframes agent-halo-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 0.25 },
          "50%": { transform: "scale(1.4)", opacity: 0.08 },
        },
        "@keyframes agent-flicker": {
          "0%, 96%, 100%": { opacity: 1 },
          "97%": { opacity: 0.55 },
          "98%": { opacity: 0.9 },
          "99%": { opacity: 0.6 },
        },
        "@keyframes agent-sweep": {
          "0%": { transform: "rotate(0deg)", opacity: 0 },
          "5%, 8%": { opacity: 0.5 },
          "15%, 100%": { opacity: 0, transform: "rotate(360deg)" },
        },
        "@keyframes agent-firing": {
          "0%, 90%, 100%": { opacity: 0 },
          "92%, 96%": { opacity: 1 },
        },
        "& .brain-outer": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 4.2s ease-in-out infinite"
            : "none",
        },
        "& .brain-mid": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath-slow 5.6s ease-in-out infinite"
            : "none",
        },
        "& .brain-inner": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 3.4s ease-in-out infinite"
            : "none",
        },
        "& .fissure": {
          animation: active
            ? "agent-fissure-flow 4.2s ease-in-out infinite"
            : "none",
        },
        "& .gyri-L": {
          animation: active
            ? "agent-gyri-fade 3.6s ease-in-out infinite"
            : "none",
        },
        "& .gyri-R": {
          animation: active
            ? "agent-gyri-fade-2 4.4s ease-in-out infinite"
            : "none",
        },
        ...Object.fromEntries(
          particles.map((p, i) => [
            `& .pt-${i}`,
            {
              animation: active
                ? `agent-pt-${i} ${p.dur}s linear infinite`
                : "none",
            },
          ])
        ),
        "& .core-pulse": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-core-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-halo": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-halo-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-flicker": {
          animation: active ? "agent-flicker 6s ease-in-out infinite" : "none",
        },
        "& .sweep": {
          transformOrigin: "40px 40px",
          animation: active ? "agent-sweep 9s linear infinite" : "none",
        },
        "& .firing-1": {
          animation: active ? "agent-firing 5s ease-out infinite" : "none",
        },
        "& .firing-2": {
          animation: active ? "agent-firing 7s ease-out infinite 2.3s" : "none",
        },
        "& .firing-3": {
          animation: active ? "agent-firing 6s ease-out infinite 4.1s" : "none",
        },
      }}
    >
      {/* 뇌 실루엣 3겹 (외곽 / 중간 / 안쪽) — 와이어 두께감 */}
      <g className="brain-outer">
        <path
          d={BRAIN_OUTLINE}
          fill={primary}
          opacity="0.04"
          stroke={soft}
          strokeWidth="0.6"
          strokeOpacity="0.4"
        />
      </g>
      <g className="brain-mid">
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.7"
          opacity="0.55"
        />
      </g>
      <g
        className="brain-inner"
        transform="translate(40 40) scale(0.93) translate(-40 -40)"
      >
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.45"
          opacity="0.35"
          strokeDasharray="1.5 1.5"
        />
      </g>

      {/* sweep 라인 — 알아차림 스캔 */}
      <g className="sweep">
        <line
          x1="40"
          y1="40"
          x2="40"
          y2="10"
          stroke={primary}
          strokeWidth="0.4"
          opacity="0.45"
        />
      </g>

      {/* 좌우 hemisphere gyri (뇌주름) */}
      <g className="gyri-L" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_LEFT.map((d, i) => (
          <path key={`gL-${i}`} d={d} />
        ))}
      </g>
      <g className="gyri-R" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_RIGHT.map((d, i) => (
          <path key={`gR-${i}`} d={d} />
        ))}
      </g>

      {/* 종단 fissure (중앙 골) — 흐르는 dash */}
      <path
        className="fissure"
        d={BRAIN_FISSURE}
        fill="none"
        stroke={primary}
        strokeWidth="0.75"
        strokeDasharray="2 1.5"
        opacity="0.55"
      />

      {/* 신경 입자 16개 — 각자 고유한 chaotic trajectory (Lissajous 합성) */}
      {particles.map((p, i) => (
        <circle
          key={`pt-${i}`}
          className={`pt-${i}`}
          cx="40"
          cy="40"
          r={p.size}
          fill={primary}
          opacity={p.opacity}
        />
      ))}

      {/* synaptic firing — 가끔 깜빡이는 점 3개 */}
      <circle
        className="firing-1"
        cx="26"
        cy="32"
        r="1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-2"
        cx="54"
        cy="38"
        r="1.1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-3"
        cx="48"
        cy="52"
        r="0.9"
        fill={primary}
        opacity="0"
      />

      {/* 중심 코어 — 의식 */}
      <g className="core-flicker">
        <g className="core-halo">
          <circle cx="40" cy="40" r="13" fill={primary} opacity="0.07" />
        </g>
        <g className="core-halo">
          <circle cx="40" cy="40" r="8" fill={primary} opacity="0.14" />
        </g>
        <g className="core-pulse">
          <circle cx="40" cy="40" r="4.5" fill={primary} opacity="0.35" />
          <circle cx="40" cy="40" r="2.6" fill={primary} />
        </g>
      </g>
    </Box>
  );
}

// =========================
// PresetScenarios — clickable bubbles to send canned messages
// =========================

export interface PresetScenario {
  id: string;
  label: string;
  message: string;
  description?: string;
}

const RAW_TEXT_BEGINNING = `다음 글을 활동지로 만들어줘 (소스 마크업: 제목 / 소제목 / 기본 / 버튼):

제목: 변화의 시작

소제목: 위대한 선택
기본: 반갑습니다. 아마 마음이 심하게 흔들리고 힘든 시기를 겪다가 이곳까지 찾아오셨을 겁니다. 그리고 이제는 정말 스스로 변해야겠다고 굳게 마음먹으셨겠지요. 그렇지 않았다면 디스턴싱이 아니라 당장 눈앞의 고통만 가려줄 임시방편을 택하셨을 테니까요.
기본: 진정한 변화를 일궈내겠다고 결심한 그 용기에 진심으로 박수를 보냅니다. 앞으로 100일 동안 이곳에서 내면의 힘을 기르며, 인생의 전환점을 맞이하시게 될 겁니다.

소제목: 마법의 알약
기본: 이곳에는 복잡한 감정을 단숨에 잠재워 줄 마법의 알약은 없습니다. 그저 글 몇 편 읽는 것만으로 엄청난 변화가 일어난다는 헛된 약속도 하지 않겠습니다. 마음을 치유하는 일은 하루아침에 뚝딱 이루어지는 가벼운 과정이 아닙니다.
기본: 하지만 이 프로그램은 당신이 스스로의 치료자가 되어 진정한 변화를 이끌어내도록 도울 수는 있습니다. 이 과정은 상담사나 약물에 기대는 것이 아니라, 내 삶을 이끄는 방법을 배우는 주도적인 훈련이 될 것입니다.
기본: 때로는 이 훈련의 과정 속에서 답답하고 좌절감을 느낄지도 모릅니다. 하지만 인내심을 가지고 끝까지 해낸다면, 그 무엇과도 바꿀 수 없는 단단한 내면의 힘을 얻게 될 것입니다.
기본: 여지껏 삶의 전환점을 맞이한 수많은 사람들도 처음에는 과연 자신이 변할 수 있을지 의심했습니다. 지금 이 단계에서 그런 의문이 드는 것은 매우 당연한 일입니다.
기본: "나도 변화할 수 있을까?" 해보지 않으면 모릅니다. 제대로 해보지 않고 평생 "나도 변화할 수 있을까?"라는 의문을 품고 사시겠습니까, 아니면 이번 기회에 나도 변화할 수 있는지 확실히 알아보시겠습니까?
기본: 변화하기로 마음 먹고 여기까지 온 것이라면, 지금부터는 스스로를 믿고 프로그램을 밟아 나아가보시길 바랍니다. 포기하고 싶은 순간이 찾아오면 언제든 코치 선생님에게 알려주세요. 코치 선생님은 당신이 이 여정을 계속해 나갈 수 있도록 최선을 다해 도울 것입니다.

소제목: 마음속 타인
기본: 누군가 내 옆을 졸졸 따라다니며 부정적이고 비판적인 말을 귓가에 끊임없이 속삭인다고 상상해 보세요. 처음엔 당황해서 꾹 참아보기도 하고, 그래도 멈추지 않으면 조용히 하라고 화도 내보겠지요. 하지만 그는 아랑곳하지 않습니다. 밀어내려 싸우고 애원해 보지만, 결국 괴로워지는 건 나 자신뿐입니다.
기본: 그 목소리는 내가 어찌할 수 없는 것입니다. 하지만 우리는 이러한 사실을 모른채 스스로에게 끔찍한 일을 저지르고 있습니다.
기본: 우린 마음속에 떠오르는 모든 생각과 감정을 밀어내려고 온갖 수를 쓰며 애를 씁니다. 그러다 결국엔 그런 부정적인 목소리를 내는 자신에게 화를 내고, 그것을 막지 못하는 스스로를 자책하며 갉아먹고 있는 것이죠.
기본: 앞으로 100일 동안 다룰 핵심 기술은 바로 '거리두기'입니다. 쉽게 말해, 머릿속에 떠오르는 생각들을 내 옆에 앉은 낯선 사람이 던지는 말이라고 여기는 연습입니다. 이 기술에 익숙해지면 생각을 관찰할 수 있게 되고, 귓가에 어떤 소리가 맴돌든 그로부터 온전히 자유로워질 수 있습니다.
기본: 단, 이것이 단순히 '주변의 잡음처럼 하찮게 여기며 무시하기'를 뜻하진 않는다는 것을 꼭 기억해주세요. 디스턴싱은 그 반대입니다. 하찮게 여기지 않을 것입니다. 그렇다고 소중히 여기지도 않을 것입니다. 판단과 평가 없이 듣고, 있는 그대로를 기꺼이 품을 것입니다. 우리는 앞으로 이 태도를 연습해 나갈 것입니다.
버튼: 디스턴싱을 배우고 나면, 머릿속에 스스로를 깎아내리는 비관적인 생각이 떠올랐을 때 어떻게 대처하게 될까요?
[A. 그 생각이 나의 진짜 모습이자 진실이라고 굳게 믿는다.]
[B. 나와 무관한 주변의 소리로 여기며 판단 없이 지그시 관찰한다.]
[C. 왜 그런 생각을 했는지 스스로를 탓하며 분석한다.]
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 B입니다. 생각은 나의 본질을 의미하지 않습니다. 그저 지금 창 밖에서 들려오는 바람 소리처럼 우연히 스쳐 지나가는 것일 뿐입니다. 이 사실을 꼭 기억해주시길 바랍니다.

소제목: 오래된 펌프
기본: 낯선 개념을 받아들이며 습관을 바로잡는 과정은 오랫동안 쓰지 않은 펌프에서 물을 끌어올리는 일과 비슷합니다. 펌프를 3년이나 방치했다면 그 안에는 엄청난 녹과 먼지가 쌓여 있겠지요. 즉, 누구나 거리두기를 연습하며 내면을 들여다보기 시작하면, 처음에는 흙먼지가 섞인 탁한 물이 쏟아져 나옵니다.
기본: 많은 분들이 이 흙탕물을 보고는 상태가 더 나빠졌다고 지레짐작하며 서둘러 펌프질을 멈춰버립니다. 하지만 이는 고여 있던 찌꺼기가 빠져나오는, 아주 자연스럽고도 꼭 필요한 중간 과정일 뿐입니다. 지금 해야 할 일은 맑은 물이 나올 때까지 멈추지 않고 계속 펌프질을 하는 것입니다.
기본: 당장 거리두기 연습이 고통스럽고 혼란스럽더라도 두려워하거나 멈추지 않아야 합니다. 내면의 탁한 습관들이 모두 흘러가도록 묵묵히 내버려 두면, 마침내 맑고 건강한 습관이 그 자리를 채울 것입니다. 맑은 물을 마시게 되는 그 감격스러운 순간까지 프로그램이 곁에서 돕겠습니다.
버튼: 훈련을 시작한 후, 갑자기 혼란스럽고 부정적인 감정이 쏟아져 나온다면 어떻게 해야 할까요?
[A. 상태가 악화되었다고 판단하고 즉시 훈련을 그만둔다.]
[B. 더 쉽고 빠른 다른 치료법이 없는지 찾아 나선다.]
[C. 자연스러운 과정이라 여기고 훈련을 계속한다.]
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 C입니다. 훈련 과정에서 내면의 찌꺼기가 빠져나오는 것은 자연스럽고 필연적인 현상입니다.

소제목: 100일간의 여정
기본: 이 힘든 시기가 오히려 단단한 성장의 발판이 될 수 있도록 끝까지 함께하겠습니다. 프로그램을 성공적으로 마치게 된다면, 훗날 비슷한 시련이 다시 찾아와도 예전처럼 무너지지 않고 의연하게 이겨낼 수 있을 것입니다. 이 100일간의 뜻깊은 여정 끝에서, 한층 성숙하고 달라진 자신과 마주하시게 될 겁니다.
버튼: 100일간의 디스턴싱 훈련 과정을 통해 얻을 수 있는 것은 무엇일까요?
[A. 당장의 불안과 스트레스를 잊게 해주는 도피처]
[B. 근본적으로 나 자신을 변화시키는 내면의 힘]
[C. 누군가의 조언으로 내 문제를 해결하는 것]
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 B입니다. 디스턴싱은 스스로 만들어내는 가장 확실하고 굳건한 변화를 추구합니다.`;

const RAW_TEXT_DISTANCING = `다음 글을 활동지로 만들어줘 (소스 마크업: 제목 / 소제목 / 기본 / 버튼):

거리두기란 무엇인가

소제목: 마음의 선글라스
기본: 마음이 건강하지 않을 때는 세상을 마치 새까만 선글라스를 끼고 보는 것과 같습니다. 아무리 기분 좋은 일이 생겨도 결국 불안이나 걱정, 깊은 무력감에 빠지고 맙니다. 뇌가 모든 상황을 부정적으로 해석하도록 습관이 들어버렸기 때문입니다.
기본: 이런 상태에서는 머릿속에 떠오르는 부정적인 생각들이 모두 변함없는 진실처럼 느껴집니다. 그 생각들을 곧 '나 자신'이라고 굳게 믿으며 스스로를 옭아매고 자책하게 되죠. 혹시 지금 이렇게 어둡고 긴 터널을 홀로 걷고 계시나요?
기본: 그렇다면 정말 잘 찾아오셨습니다. 이제부터 그 까만 마음의 선글라스를 벗고 본래의 삶을 되찾는 연습을 시작해보려 합니다. 나를 괴롭히던 그 생각들이 사실 '나' 자신이 아니었다는 것을 자연스럽게 깨닫게 될 것입니다.

소제목: 신성한 멈춤
기본: 생각은 순식간에 지나갑니다. 그런 생각을 미처 알아차리기도 전에 우린 이미 그에 반응해서 감정과 감각을 느끼며 어떤 행동을 마친 상태이지요. 이렇게 무의식적으로 반응하는 악순환을 끊어내려면 가장 먼저 '잠시 멈추는 연습'이 필요합니다. 이를 일상 속의 '신성한 멈춤'이라고 합니다.
기본: 신성한 멈춤을 갖게 되면, 생각과 자동 반응 사이의 연결고리가 끊어집니다. 비로소 내 진짜 감정이 두려움인지 슬픔인지 한 걸음 물러서서 관찰할 수 있는 틈이 생기는 것이죠. 다툼이나 갈등이 일어나는 순간에도, 이 멈춤은 현명한 선택을 내릴 수 있는 마음의 여유를 만들어줍니다.
기본: 이러한 멈춤은 마음이 비교적 평온할 때 일상 속에서 미리 연습해 두는 것이 좋습니다. 평소에 충분히 훈련해 두면, 거대한 스트레스가 닥쳐와도 흔들림 없이 차분하게 대처할 수 있습니다.
버튼: 무의식적인 반응을 끊어내기 위해 가장 먼저 해야 할 일은 무엇일까요?
[A. 내 잘못이 무엇인지 자책한다.]
[B. 반박할 말을 머릿속으로 계산한다.]
[C. 긍정적인 상황이라고 세뇌한다.]
[D. 마음속에서 일어나는 일들을 관찰한다.]
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 D입니다. '신성한 멈춤'을 통해 습관적으로 반응하는 연결고리를 끊어낼 수 있습니다.

소제목: 마음의 RAIN
기본: 그렇다면 이 '신성한 멈춤'은 어떻게 실천할 수 있을까요? 신성한 멈춤은 RAIN이라는 방법을 통해 실천할 수 있습니다. RAIN의 과정은 다음과 같습니다.
인지하기 (R, Recognition)
내 마음에서 무슨 일이 일어나는지 알아차리기
수용하기 (A, Acceptance)
마음속에 떠오른 감정이나 생각을 거부하지 않고 온전히 받아들이기
살펴보기 (I, Investigation)
그 마음의 움직임을 어린아이와 같은 호기심으로 가만히 살펴보기
탈동일시 (N, Non-identification)
그 생각들을 나 자신과 동일시하지 않기
기본: 이 네 가지 단계를 거치다 보면, 거센 생각의 소용돌이 속에서도 내가 진정으로 원하는 삶의 방향을 선택할 힘을 얻게 됩니다. 마음에 비바람이 몰아칠 때마다 이 'RAIN'을 떠올려 보세요. 해묵은 마음의 습관과 문제 행동에서 벗어나 자유로워지는 열쇠가 되어줄 것입니다. 앞으로 이 네 가지 과정에 따라 거리두기 기술을 차근차근 연습해 보겠습니다.

소제목: 건강한 마음에 대한 오해
기본: 흔히 마음이 건강해진다는 것을 '늘 긍정적인 생각만 가득한 상태'로 오해하곤 합니다. 하지만 억지로 긍정적인 생각만 하려 애쓰는 것은 그리 좋은 방법이 아닙니다. 일시적인 눈속임에 불과할 뿐만 아니라, 오히려 문제를 악화시킬 수 있기 때문입니다.
기본: 가끔 "아무 생각도 하지 마. 그럼 편해져"라고 말하는 사람들도 있습니다. 하지만 생각을 완전히 없애는 것은 애초에 불가능합니다. 사람의 뇌는 무언가를 '지우는' 방식으로는 작동하지 않으니까요. 따라서 이 역시 효과적인 해결책이 될 수 없습니다.
기본: 이 부분은 나중에 더 자세히 다루겠습니다. 지금은 앞으로 연습할 내용이 '무조건 긍정적으로 생각하기'나 '아무 생각도 하지 않기'가 아니라는 점만 기억해 두시면 충분합니다.

소제목: 새로운 관계
기본: 우리를 견딜 수 없이 힘들게 만드는 진짜 원인은 슬픔이나 불안 같은 '감정' 그 자체가 아닙니다. 그 감정을 다루고 관계 맺는 '방식'에 있습니다. 부정적인 감정이나 생각을 억누르고 없애려고만 하면, 그것들은 더 강하게 마음을 옭아맵니다.
기본: 이제부터는 억지로 생각을 지우려 애쓰는 대신, 마음속에 떠오르는 생각들과 '건강하게 관계 맺는 연습'을 해나갈 것입니다. 생각과 적당한 거리를 두고 유연하게 대처할 수 있게 된다면, 앞으로 어떤 시련이 닥쳐와도 내 삶을 지혜롭게 이끌어갈 수 있을 것입니다.
버튼: 오늘부터 새롭게 연습해 나갈 내용은 무엇일까요?
[A. 매일 스스로에게 긍정적인 말 해주기]
[B. 아무 생각 없이 머릿속을 비워내기]
[C. 마음속 생각 및 감정들과 건강한 관계 맺기]
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 C입니다. 억지로 긍정적인 생각을 하거나 생각을 지우는 것은 불가능하며 효과적이지도 않습니다. 유일한 해결책은 내 생각과 감정을 대하는 관점을 바꾸는 것입니다.`;

const RAW_TEXT_EXPERIENCE_SEPARATION = `다음 글을 활동지로 만들어줘 (소스 마크업: 제목 / 소제목 / 기본 / 버튼 / 이미지):

기술 1: 경험 분리

소제목: 첫 번째 훈련법
기본: 지난 시간에 마음을 인지하는 두 가지 훈련 방법이 있다고 배웠습니다. 기록을 통해 경험을 분리하는 연습과, 명상으로 주의력을 통제하는 훈련이었죠. 오늘은 그중 첫 번째 연습을 본격적으로 시작해 보겠습니다.
기본: 이번 시간에 가장 먼저 배울 핵심 개념은 심리학에서 매우 중요하게 다루는 '경험 분리'입니다. 마음의 작용을 이해하기 위한 아주 기초적인 작업입니다. 스포츠나 악기를 처음 배울 때 기본기가 중요한 것처럼, 이 기본기만 확실히 익혀두어도 마음과 거리 두는 능력이 몰라보게 탄탄해질 것입니다.

소제목: 복잡한 경험 꾸러미
기본: 마음속에 떠오르는 부정적인 생각과 거리를 둔 채 가만히 바라보기란 말처럼 쉽지 않습니다. 현실에서 마주하는 내면의 경험이 단순히 '생각'이라는 단일한 덩어리로만 이루어져 있지 않기 때문입니다.
기본: 사실 마음속에서는 생각과 감정, 신체 반응, 그리고 행동과 충동이 복잡하게 뒤섞여 하나의 거대한 '꾸러미'처럼 한꺼번에 쏟아집니다.
🖼️ (상황 → 경험 꾸러미)
기본: 예를 들어 직장에서 무언가 실수를 한 상황을 떠올려 볼까요? "이번 프로젝트는 나 때문에 다 망했어"라는 생각과 동시에 죄책감이나 불안 같은 강렬한 감정이 파도처럼 밀려옵니다. 가슴이 답답해지거나 머리가 아픈 증상이 나타나기도 하고, 당장이라도 다 포기하고 도망치고 싶다는 충동까지 일어납니다. 이 모든 것이 하나의 꾸러미로 묶여서 다가오는 것이죠.
버튼: 다음 중 내면에서 뭉쳐지는 '경험 꾸러미'의 구성 요소가 아닌 것은 무엇일까요?
[A. 머릿속을 스쳐가는 부정적인 '생각']
[B. 불안하고 초조한 '감정']
[C. 도망치고 싶어지는 '충동']
[D. 실수를 저지른 객관적인 '상황']
(오답 시: 활동지를 천천히 살펴본 뒤 다시 답해보세요.)
기본: 정답은 D입니다. 실수를 저지른 '상황'은 내면의 반응을 촉발하는 외부 사건일 뿐, 경험 꾸러미에 속하지 않습니다. 경험 꾸러미는 특정 상황을 마주했을 때 마음속에서 덩어리져 일어나는 생각, 감정/신체 반응, 행동/충동으로 구성됩니다.

소제목: 끝없는 악순환
기본: 경험 꾸러미 속의 생각, 감정과 신체 반응, 행동과 충동은 멈춰 있지 않고 서로 끊임없이 영향을 주고받습니다. 부정적인 생각이 불안을 증폭시키고, 그 불안은 호흡을 가쁘게 만들어 더 극단적인 생각을 불러오기도 합니다. 마치 엉켜버린 털실 타래처럼 단단하게 뭉쳐서 끝없는 악순환을 만들어냅니다.
기본: 이렇게 얽힌 꾸러미를 그대로 둔 채 생각과 거리를 두기란 거의 불가능합니다. 마음속에 정확히 어떤 생각이 스쳐 지나갔는지 파악하기 어려울 뿐만 아니라, 막연한 감정의 크기가 너무 압도적으로 느껴지기 때문입니다. 따라서 이 거대한 덩어리를 각각의 개별 요소로 가닥가닥 쪼개어 바라보는 연습이 필요합니다.
기본: 위의 직장에서 무언가 실수를 한 상황 예시에서 '경험 꾸러미'를 분리한 모습은 다음과 같습니다.
생각
이번 프로젝트는 나 때문에 다 망했어
감정
죄책감, 불안
신체 반응
가슴이 답답함, 머리가 아픔
행동/충동
포기하고 도망치고 싶었음

소제목: 실전 연습
기본: 이처럼 얽혀 있는 마음의 덩어리를 개별 요소로 해체하는 과정을 '경험 분리'라고 부릅니다. 방법은 간단하지만 효과는 매우 강력합니다. 압도적인 감정에 휩쓸리지 않고 마음을 객관적으로 바라볼 수 있게 돕기 때문입니다.
기본: 앞으로 벅차고 강렬한 감정을 마주하더라도, 일기장에 감정을 쏟아내듯 무작정 적어 내려가는 것은 피하는 게 좋습니다. 그 대신, 객관적인 '상황', 머릿속을 스쳐 간 '생각', 느껴진 '감정과 신체 반응', 그리고 '충동이나 실제 행동'을 명확히 나누어 기록해 보세요.
기본: 이렇게 항목을 구분해 적는 것만으로도 혼란스러웠던 경험 꾸러미의 힘이 약해지고 마음에 여유가 생기는 것을 느낄 수 있습니다. 자, 그럼 다음 활동에서 이 '경험 분리'를 구체적으로 어떻게 실천할 수 있는지 함께 알아보겠습니다.`;

export const PRESET_SCENARIOS: PresetScenario[] = [
  {
    id: "rawToBeginning",
    label: "긴 글 → 활동지 (1)",
    message: RAW_TEXT_BEGINNING,
    description:
      '"변화의 시작" — Header1 × 6 + Instruction × 11 + LastCard. Quiz × 3 (모두 정답 해설 동반).',
  },
  {
    id: "rawToDistancing",
    label: "긴 글 → 활동지 (2)",
    message: RAW_TEXT_DISTANCING,
    description:
      '"거리두기란 무엇인가" — Label-Body 페어 + Quiz × 2 (pause_quiz / newrel_quiz).',
  },
  {
    id: "rawToExperienceSeparation",
    label: "긴 글 → 활동지 (3)",
    message: RAW_TEXT_EXPERIENCE_SEPARATION,
    description:
      '"기술 1: 경험 분리" — Image placeholder + 4-항목 분리 예시 (Label-Body × 4) + Quiz × 1 (bundle_quiz).',
  },
  {
    id: "branchingFlow",
    label: "분기 추가",
    message: "ButtonGroup 분기 추가해줘 — 두 갈래 경로로",
    description:
      "ButtonGroup 옵션의 showFromId 로 두 경로 블록을 연결, 모두 LastCard 로 수렴",
  },
  {
    id: "validate",
    label: "검증",
    message: "현재 활동지 가이드 규칙대로 검증해줘",
    description:
      "ID 고유성 / LastCard 위치 / Instruction Typography 위계 / snake_case / showFromId 무결성 lint",
  },
  {
    id: "addQuiz",
    label: "Quiz 추가",
    message: "Quiz 컴포넌트 추가해줘",
    description:
      "마지막 Instruction 에 Quiz 1줄 추가 (정답 표시 + incorrectMessage)",
  },
  {
    id: "removeBlock",
    label: "블록 삭제",
    message: "마지막 블록 삭제해줘",
    description: "LastCard 직전 블록 1개 제거",
  },
  {
    id: "help",
    label: "도움말",
    message: "도움말",
    description: "mock 안내 + 운영 Agent 역할 / 가이드 규칙 요약",
  },
];

export function PresetScenariosBar({
  onPick,
  disabled,
}: {
  onPick: (scenario: PresetScenario) => void;
  disabled: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        px: "12px",
        pt: "8px",
        pb: "4px",
        borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
      }}
    >
      <Typography
        level="body-xs"
        sx={{
          fontSize: "10px",
          color: theme.vars.palette.Content.Weak,
          alignSelf: "center",
          mr: "2px",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        시나리오
      </Typography>
      {PRESET_SCENARIOS.map((scenario) => (
        <Box
          key={scenario.id}
          onClick={() => !disabled && onPick(scenario)}
          title={scenario.description}
          sx={{
            px: "10px",
            py: "4px",
            borderRadius: "999px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontSize: "11px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            transition: "all 0.15s ease",
            userSelect: "none",
            "&:hover": disabled
              ? {}
              : {
                  backgroundColor: theme.vars.palette.Background.Level1,
                  borderColor: theme.vars.palette.Content.Medium,
                  color: theme.vars.palette.Content.Strong,
                  transform: "translateY(-1px)",
                },
          }}
        >
          {scenario.label}
        </Box>
      ))}
    </Box>
  );
}

// =========================
// UserBubble
// =========================

export function UserBubble({ turn }: { turn: UserTurn }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: "12px",
          py: "8px",
          borderRadius: "14px 14px 4px 14px",
          backgroundColor: theme.vars.palette.primary.solid,
          color: "#fff",
          fontSize: "13px",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {turn.content}
      </Box>
    </Box>
  );
}

// =========================
// ToolCallRow — line status with expandable result
// =========================

function summarizeToolArgs(
  name: string,
  args: Record<string, unknown>
): string {
  if (typeof args.file_path === "string") {
    const parts = args.file_path.split("/");
    return parts[parts.length - 1] || args.file_path;
  }
  if (typeof args.path === "string") return args.path;
  return Object.keys(args).slice(0, 2).join(", ") || "";
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function ToolCallRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "tool" }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const running = step.status === "running";
  const argsSummary = summarizeToolArgs(step.name, step.args);
  const duration =
    step.finishedAt && step.startedAt
      ? formatDuration(step.finishedAt - step.startedAt)
      : null;
  const hasResult = !!step.result;

  return (
    <Box
      onClick={() => hasResult && setExpanded((v) => !v)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        px: "8px",
        py: "5px",
        borderRadius: "6px",
        cursor: hasResult ? "pointer" : "default",
        transition: "background-color 0.15s",
        "&:hover": hasResult
          ? { backgroundColor: theme.vars.palette.Background.Level2 }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: running ? theme.vars.palette.primary.solid : "#15803d",
            flexShrink: 0,
          }}
        >
          {running ? (
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: `1.5px solid ${theme.vars.palette.Background.Level3 ?? "#ddd"}`,
                borderTopColor: theme.vars.palette.primary.solid,
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": {
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ) : (
            <Box
              component="span"
              sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 700 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 600,
          }}
        >
          {step.name}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {argsSummary}
        </Box>
        {duration && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {duration}
          </Box>
        )}
        {hasResult && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {expanded ? "▾" : "▸"}
          </Box>
        )}
      </Box>
      {expanded && step.result && (
        <Box
          sx={{
            mt: "6px",
            ml: "20px",
            p: "8px 10px",
            borderRadius: "4px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "11px",
            lineHeight: 1.5,
            color: theme.vars.palette.Content.Medium,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          {step.result}
        </Box>
      )}
    </Box>
  );
}

// =========================
// TextStreamBubble
// =========================

export function TextStreamBubble({
  step,
  isLast,
  isStreaming,
}: {
  step: Extract<AssistantStep, { kind: "text" }>;
  isLast: boolean;
  isStreaming: boolean;
}) {
  return (
    <Box
      sx={{
        px: "12px",
        py: "8px",
        borderRadius: "4px 14px 14px 14px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        fontSize: "13px",
        lineHeight: 1.6,
        color: theme.vars.palette.Content.Strong,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        animation: "fadeIn 0.2s ease",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {step.content}
      {isLast && isStreaming && (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "6px",
            height: "13px",
            backgroundColor: theme.vars.palette.Content.Strong,
            verticalAlign: "text-bottom",
            ml: "2px",
            animation: "blink 1s steps(2) infinite",
            "@keyframes blink": {
              "0%, 50%": { opacity: 1 },
              "50.01%, 100%": { opacity: 0 },
            },
          }}
        />
      )}
    </Box>
  );
}

// =========================
// ApprovalDecisionStep — 사용자가 내린 결정 표시
// =========================

export function ApprovalDecisionRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "approval_decision" }>;
}) {
  const decisionLabel =
    step.decision.type === "approve"
      ? "✓ 승인"
      : step.decision.type === "edit"
        ? "✎ 수정 후 적용"
        : "✕ 거부";
  const decisionColor =
    step.decision.type === "approve"
      ? "#15803d"
      : step.decision.type === "edit"
        ? theme.vars.palette.primary.solid
        : "#b91c1c";
  const rejectMessage =
    step.decision.type === "reject" ? step.decision.message : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: decisionColor,
          fontWeight: 700,
        }}
      >
        {decisionLabel}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {step.originalAction.name}
      </Box>
      {rejectMessage && (
        <Box
          component="span"
          sx={{
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          “{rejectMessage}”
        </Box>
      )}
    </Box>
  );
}

// =========================
// ApprovalCard
// =========================

export function ApprovalCard({
  data,
  onDecide,
}: {
  data: PendingApprovalData;
  onDecide: (decisions: Decision[]) => void;
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: "12px",
        mt: "4px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Background.Level1,
        border: `1px solid ${theme.vars.palette.primary.solid}`,
        animation: "approvalPop 0.3s ease",
        "@keyframes approvalPop": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.vars.palette.primary.solid,
            animation: "pulse 1.5s ease infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          level="body-xs"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: theme.vars.palette.primary.solid,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          승인 필요
        </Typography>
      </Stack>
      {data.action_requests.map((action, idx) => (
        <ApprovalItem
          key={idx}
          action={action}
          config={data.review_configs[idx]}
          onApprove={() => onDecide([{ type: "approve" }])}
          onEdit={(editedArgs) =>
            onDecide([
              {
                type: "edit",
                edited_action: { name: action.name, args: editedArgs },
              },
            ])
          }
          onReject={(message) => onDecide([{ type: "reject", message }])}
        />
      ))}
    </Stack>
  );
}

function ApprovalItem({
  action,
  config,
  onApprove,
  onEdit,
  onReject,
}: {
  action: ActionRequest;
  config?: ReviewConfig;
  onApprove: () => void;
  onEdit: (args: Record<string, unknown>) => void;
  onReject: (message: string) => void;
}) {
  const [mode, setMode] = useState<"idle" | "edit" | "reject">("idle");
  const [argsText, setArgsText] = useState(() =>
    JSON.stringify(action.args, null, 2)
  );
  const [rejectMessage, setRejectMessage] = useState("");

  const allowed = config?.allowed_decisions ?? ["approve", "edit", "reject"];

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
          color: theme.vars.palette.Content.Strong,
          fontWeight: 600,
        }}
      >
        {action.name}
        <Box
          component="span"
          sx={{
            ml: "8px",
            color: theme.vars.palette.Content.Medium,
            fontWeight: 400,
          }}
        >
          {typeof action.args.file_path === "string"
            ? String(action.args.file_path)
            : ""}
        </Box>
      </Box>

      <Typography
        level="body-sm"
        sx={{
          fontSize: "13px",
          color: theme.vars.palette.Content.Medium,
          lineHeight: 1.5,
        }}
      >
        {action.description}
      </Typography>

      <ActionArgsPreview args={action.args} />

      {mode === "idle" && (
        <Stack direction="row" spacing={1}>
          {allowed.includes("approve") && (
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={onApprove}
              sx={{ minHeight: "30px", fontSize: "12px", fontWeight: 600 }}
            >
              ✓ Approve
            </Button>
          )}
          {allowed.includes("edit") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("edit")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✎ Edit
            </Button>
          )}
          {allowed.includes("reject") && (
            <Button
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => setMode("reject")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✕ Reject
            </Button>
          )}
        </Stack>
      )}

      {mode === "edit" && (
        <Stack spacing={1}>
          <Textarea
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            minRows={6}
            maxRows={14}
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => {
                try {
                  const parsed = JSON.parse(argsText);
                  onEdit(parsed);
                } catch {
                  // ignore parse error — leave UI open
                }
              }}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}

      {mode === "reject" && (
        <Stack spacing={1}>
          <Textarea
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="왜 거부하시나요? agent 에게 전달됩니다"
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "12px" }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="danger"
              onClick={() => onReject(rejectMessage.trim())}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              거부 전송
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function ActionArgsPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const isWriteFile = typeof args.content === "string";
  const isEditFile =
    typeof args.old_string === "string" && typeof args.new_string === "string";

  if (isEditFile) {
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} diff
        </Box>
        {expanded && (
          <Box
            sx={{
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                color: "#b91c1c",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
              }}
            >
              − {String(args.old_string)}
            </Box>
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(34, 197, 94, 0.08)",
                color: "#15803d",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              + {String(args.new_string)}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (isWriteFile) {
    const content = String(args.content);
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} content ({content.length} chars)
        </Box>
        {expanded && (
          <Box
            sx={{
              p: "8px 10px",
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              lineHeight: 1.5,
              maxHeight: "240px",
              overflow: "auto",
              whiteSpace: "pre",
            }}
          >
            {content}
          </Box>
        )}
      </Box>
    );
  }

  return null;
}

// =========================
// TaskPatchPill
// =========================

export function TaskPatchPill() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        alignSelf: "flex-start",
        px: "8px",
        py: "3px",
        mt: "2px",
        borderRadius: "999px",
        backgroundColor: theme.vars.palette.primary.soft,
        color: theme.vars.palette.primary.solid,
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        animation: "patchSlide 0.3s ease",
        "@keyframes patchSlide": {
          from: { opacity: 0, transform: "translateY(-2px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box component="span">✎</Box>
      활동지 업데이트됨
    </Box>
  );
}

// =========================
// DebugFooter
// =========================

export function DebugFooter({
  debug,
}: {
  debug: NonNullable<AssistantTurn["debug"]>;
}) {
  const copyTrace = () => {
    if (debug.trace_id && typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(debug.trace_id);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mt: "4px",
        fontSize: "10px",
        color: theme.vars.palette.Content.Weak,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      <Box component="span">⏱ {formatDuration(debug.execution_time_ms)}</Box>
      {debug.trace_id && (
        <Box
          component="span"
          onClick={copyTrace}
          title="클릭해서 trace id 복사"
          sx={{
            cursor: "pointer",
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          · trace {debug.trace_id}
        </Box>
      )}
    </Box>
  );
}

// =========================
// AssistantTurnView
// =========================

export function AssistantTurnView({
  turn,
  isLast,
  isStreaming,
  onDecide,
}: {
  turn: AssistantTurn;
  isLast: boolean;
  isStreaming: boolean;
  onDecide: (decisions: Decision[]) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {turn.steps.map((step, idx) => {
        const isLastStep = idx === turn.steps.length - 1;
        if (step.kind === "tool") {
          return <ToolCallRow key={step.id} step={step} />;
        }
        if (step.kind === "text") {
          return (
            <TextStreamBubble
              key={step.id}
              step={step}
              isLast={isLast && isLastStep}
              isStreaming={isStreaming && isLast && isLastStep}
            />
          );
        }
        return <ApprovalDecisionRow key={step.id} step={step} />;
      })}

      {turn.status === "awaiting_approval" && turn.pendingApproval && (
        <ApprovalCard data={turn.pendingApproval} onDecide={onDecide} />
      )}

      {turn.taskChanged && <TaskPatchPill />}

      {turn.error && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "6px",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: `1px solid rgba(239, 68, 68, 0.3)`,
            fontSize: "12px",
            color: "#b91c1c",
          }}
        >
          ✕ {turn.error.message}
        </Box>
      )}

      {turn.debug && <DebugFooter debug={turn.debug} />}
    </Box>
  );
}

// =========================
// ChatInput
// =========================

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  placeholder?: string;
}) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit();
    }
  };
  return (
    <Box
      sx={{
        p: "10px 12px 12px",
        display: "flex",
        alignItems: "flex-end",
        gap: "8px",
        backgroundColor: theme.vars.palette.Surface.Forward,
      }}
    >
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        minRows={1}
        maxRows={5}
        disabled={disabled}
        sx={{
          flex: 1,
          fontSize: "13px",
          borderRadius: "8px",
        }}
      />
      <IconButton
        variant="solid"
        color="primary"
        onClick={onSubmit}
        disabled={disabled || !value.trim()}
        sx={{
          minHeight: "36px",
          minWidth: "36px",
          fontSize: "16px",
          borderRadius: "8px",
        }}
        title="전송 (Enter)"
      >
        ↑
      </IconButton>
    </Box>
  );
}
